import {
  composeRenderProps,
  type DateFieldProps,
  DateField as DateFieldRac,
  type DateInputProps as DateInputPropsRac,
  DateInput as DateInputRac,
  type DateSegmentProps,
  DateSegment as DateSegmentRac,
  type DateValue as DateValueRac,
  type TimeFieldProps,
  TimeField as TimeFieldRac,
  type TimeValue as TimeValueRac,
} from 'react-aria-components'

import { cn } from '@/infra'

function DateField<T extends DateValueRac>({ className, children, ...props }: Readonly<DateFieldProps<T>>) {
  return (
    <DateFieldRac className={composeRenderProps(className, (_className) => cn(_className))} {...props}>
      {children}
    </DateFieldRac>
  )
}

function TimeField<T extends TimeValueRac>({ className, children, ...props }: Readonly<TimeFieldProps<T>>) {
  return (
    <TimeFieldRac className={composeRenderProps(className, (_className) => cn(_className))} {...props}>
      {children}
    </TimeFieldRac>
  )
}

function DateSegment({ className, ...props }: Readonly<DateSegmentProps>) {
  const ehSeparadorDeData = props.segment.type === 'literal' && props.segment.text === '-'

  return (
    <DateSegmentRac
      className={composeRenderProps(className, (_className) =>
        cn(
          'data-invalid:data-focused:bg-destructive data-invalid:data-placeholder:text-destructive data-invalid:text-destructive data-placeholder:text-muted-foreground/70 data-[type=literal]:text-muted-foreground/70 inline rounded p-0.5 caret-transparent outline-hidden data-disabled:cursor-not-allowed data-disabled:opacity-50 data-focused:bg-sky-200 data-focused:text-sky-800 data-invalid:data-focused:text-white data-focused:data-placeholder:text-sky-800 data-invalid:data-focused:data-placeholder:text-white data-[type=literal]:px-0',
          _className,
        ),
      )}
      {...props}
      data-invalid
    >
      {ehSeparadorDeData ? '/' : props.segment.text}
    </DateSegmentRac>
  )
}

const dateInputStyle =
  'relative inline-flex h-9 w-full items-center overflow-hidden whitespace-nowrap rounded-md border border-input px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none data-focus-within:border-ring data-focus-within:ring-ring/15 data-focus-within:ring-[3px] data-focus-within:has-aria-invalid:ring-destructive/20 dark:data-focus-within:has-aria-invalid:ring-destructive/40 data-focus-within:has-aria-invalid:border-destructive'

interface DateInputProps extends DateInputPropsRac {
  className?: string
  unstyled?: boolean
}

function DateInput({ className, unstyled = false, ...props }: Readonly<Omit<DateInputProps, 'children'>>) {
  return (
    <DateInputRac
      data-testid="date-input"
      className={composeRenderProps(className, (_className) => cn(!unstyled && dateInputStyle, _className))}
      {...props}
    >
      {(segment) => <DateSegment segment={segment} />}
    </DateInputRac>
  )
}

export { DateField, DateInput, DateSegment, TimeField, dateInputStyle }
export type { DateInputProps }
